import React, { useContext, useEffect, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { Field, Form } from 'react-final-form';
import { AccountCircle, Lock, Paid, School } from '@mui/icons-material';
import { Box, Chip, Stack, Typography } from '@mui/material';
import PaymentTabPanel from 'components/trainer/profile/PaymentTabPanel';
import PersonalTabPanel from 'components/trainer/profile/PersonalTabPanel';
import ProfessionalTabPanel from 'components/trainer/profile/ProfessionalTabPanel';
import SecurityTabPanel from 'components/client/profile/SecurityTabPanel';
import PremiumCard from 'components/shared/surfaces/PremiumCard';
import ProfilePictureFieldAdapter from 'components/shared/field-adapters/ProfilePictureFieldAdapter';
import PrimaryLoadingButton from 'components/shared/buttons/PrimaryLoadingButton';
import SEOPageTitle from 'components/shared/SEOPageTitle';
import TabPanel from 'components/shared/tabs/TabPanel';
import PillTabs from 'components/shared/tabs/PillTabs';
import requestStatus from 'common/constants/status';
import getDataToSave from 'common/form/dataToSave';
import { updateTrainerProfileInitialValues } from 'common/form/initial-values/update';
import { getFieldsToUpdate } from 'common/form/utils';
import StyledIncompleteTrainerProfileChip from 'common/theme/chips/StyledIncompleteTrainerProfileChip';
import StyledButtonsContainer from 'common/theme/containers/StyledButtonsContainer';
import StyledProfileButtonContainer from 'common/theme/profile/StyledProfileButtonContainer';
import StyledProfilePictureContainer from 'common/theme/profile/StyledProfilePictureContainer';
import StyledProfileUsername from 'common/theme/profile/StyledProfileUsername';
import validateEditTrainerProfileForm from 'common/validations/form/editTrainerProfile';
import { getCountryFromJson } from 'common/utils/geo';
import { getSpecialties } from 'common/utils/user';
import AppBarContext from 'context/AppBarContext';
import authSlice from 'store/modules/auth';
import geoSlice from 'store/modules/geo';
import sessionTypeSlice from 'store/modules/sessionType';

function TrainerProfile() {
  const dispatch = useDispatch();
  const { t } = useTranslation(['buttons', 'labels', 'routes']);
  const pageTitle = t('profile', { ns: 'routes' });
  const [initialValues, setInitialValues] = useState({ languages: [], specialties: [] });
  const [currentTab, setCurrentTab] = useState(0);
  const { setAppBarButton, setAppBarTitle } = useContext(AppBarContext);

  const countries = useSelector((state) => state.geo.countries);
  const geoStatus = useSelector((state) => state.geo.status);
  const sessionTypeOptions = useSelector((state) => state.sessionType.sessionTypeOptions);
  const sessionTypeStatus = useSelector((state) => state.sessionType.status);
  const status = useSelector((state) => state.auth.status);
  const user = useSelector((state) => state.auth.user);

  useEffect(() => {
    dispatch(sessionTypeSlice.actions.listAll());
  }, [dispatch]);

  useEffect(() => {
    if (user && Object.keys(user).length > 0) {
      const userData = updateTrainerProfileInitialValues(user);
      setInitialValues(userData);
    }
  }, [user]);

  useEffect(() => {
    if (geoStatus !== requestStatus.loading && countries.length <= 0) {
      dispatch(geoSlice.actions.getCountries());
    }
  }, []);

  useEffect(() => {
    setAppBarButton(null);
    setAppBarTitle(null);
  }, [setAppBarButton, setAppBarTitle]);

  const onTabChange = (event, newValue) => {
    setCurrentTab(newValue);
  };

  const onUpdate = (values) => {
    const fieldsToUpdate = getFieldsToUpdate(values, initialValues);
    const dataToSave = getDataToSave(fieldsToUpdate, 'user');
    dispatch(authSlice.actions.updateMe({ id: user.id, input: dataToSave }));
  };

  return (
    <>
      <SEOPageTitle title={pageTitle} />
      <Form
        initialValues={initialValues}
        onSubmit={onUpdate}
        validate={validateEditTrainerProfileForm}
        render={({ handleSubmit, invalid, pristine }) => (
          <form onSubmit={handleSubmit}>
            <PremiumCard sx={{ mb: { xs: 2, lg: 4 } }} padding={{ xs: 3, md: 4 }}>
              <Stack alignItems="center" spacing={1.5} sx={{ textAlign: 'center' }}>
                <StyledProfilePictureContainer>
                  <Field
                    id="photo"
                    name="photo"
                    component={ProfilePictureFieldAdapter}
                    firstName={user.first_name}
                    lastName={user.last_name}
                  />
                </StyledProfilePictureContainer>
                <StyledProfileUsername>{user.full_name}</StyledProfileUsername>
                {user.country && (
                  <Chip
                    size="small"
                    label={`${t('country', { ns: 'labels' })}: ${getCountryFromJson(user.country).label}`}
                    sx={{ bgcolor: 'surface.raised', color: 'text.primary', border: '1px solid', borderColor: 'hairline.main', fontWeight: 600 }}
                  />
                )}
                {!user.profile_complete && (
                  <StyledIncompleteTrainerProfileChip
                    label={t('profile.incomplete', { ns: 'errors' })}
                    color="error"
                  />
                )}
                {user.specialties && getSpecialties(user.specialties) && (
                  <Box sx={{ width: '100%' }}>
                    <Typography variant="caption" sx={{ color: 'text.secondary', textTransform: 'uppercase', letterSpacing: '0.06em', fontWeight: 700, display: 'block', mb: 1 }}>
                      {t('specialties', { ns: 'labels' })}
                    </Typography>
                    <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap justifyContent="center">
                      {getSpecialties(user.specialties).split(',').map((s) => s.trim()).filter(Boolean).map((spec) => (
                        <Chip key={spec} label={spec} size="small" sx={{ bgcolor: 'gold.soft', color: 'gold.main', fontWeight: 600, border: '1px solid', borderColor: 'hairline.main' }} />
                      ))}
                    </Stack>
                  </Box>
                )}
              </Stack>
            </PremiumCard>
            <PillTabs
              value={currentTab}
              onChange={onTabChange}
              centered
              ariaLabel={t('personalInformation', { ns: 'labels' })}
              tabs={[
                {
                  key: 'personal',
                  icon: <AccountCircle />,
                  label: t('personalInformation', { ns: 'labels' })
                },
                {
                  key: 'professional',
                  icon: <School />,
                  label: t('professionalInformation', { ns: 'labels' })
                },
                {
                  key: 'payment',
                  icon: <Paid />,
                  label: t('paymentInformation', { ns: 'labels' })
                },
                {
                  key: 'security',
                  icon: <Lock />,
                  label: t('securityInformation', { ns: 'labels' })
                }
              ]}
            />
            <TabPanel current={currentTab} index={0}>
              <PersonalTabPanel countries={countries} geoStatus={geoStatus} />
            </TabPanel>
            <TabPanel current={currentTab} index={1}>
              <ProfessionalTabPanel
                sessionTypeOptions={sessionTypeOptions}
                sessionTypeStatus={sessionTypeStatus}
              />
            </TabPanel>
            <TabPanel current={currentTab} index={2}>
              <PaymentTabPanel />
            </TabPanel>
            <TabPanel current={currentTab} index={3}>
              <SecurityTabPanel />
            </TabPanel>
            <StyledProfileButtonContainer>
              <StyledButtonsContainer>
                <PrimaryLoadingButton
                  disabled={invalid || pristine || status === requestStatus.loading}
                  loading={status === requestStatus.loading}
                  label={t('save', { ns: 'buttons' })}
                  type="submit"
                />
              </StyledButtonsContainer>
            </StyledProfileButtonContainer>
          </form>
        )}
      />
    </>
  );
}

export default TrainerProfile;
